import { Button } from '@foam/buoy/src/components/Button/Button';
import { BrandfetchBrand } from '~/api/brandfetch';

interface SearchDropdownProps {
  searchTerm: string;
  brands: BrandfetchBrand[];
  isLoading: boolean;
  onShowAvailableLogos: (brand: BrandfetchBrand) => void;
  onResetSearch: () => void;
}

export const SearchDropdown = ({
  searchTerm,
  brands,
  isLoading,
  onShowAvailableLogos,
  onResetSearch,
}: SearchDropdownProps) => {
  if (!searchTerm || searchTerm.length < 2) {
    return null;
  }

  const renderHeader = () => (
    <div className="p-3">
      <h3 className="text-root-size-body-xl font-500 text-neutral-700">
        Suggested brands
      </h3>
    </div>
  );

  const renderContent = () => {
    if (isLoading) {
      return <div className="p-4 text-center text-neutral-600">Loading...</div>;
    }

    if (brands.length > 0) {
      return (
        <div className="py-1">
          {brands.map((brand, index) => (
            <div
              key={index}
              className="flex cursor-pointer items-center px-3 py-2 hover:bg-neutral-50"
              onClick={() => onShowAvailableLogos(brand)}
            >
              <div className="mr-3 size-8 shrink-0">
                <img
                  src={brand.icon}
                  alt={brand.name}
                  className="size-full rounded-4 object-contain"
                  onError={(e) => {
                    // Fallback to a placeholder if image fails to load
                    e.currentTarget.src = '/images/brand-logos/placeholder.png';
                  }}
                />
              </div>
              <div className="min-w-0 flex-1">
                <h4 className="truncate text-root-size-body-xl font-500 leading-1 text-neutral-900">
                  {brand.name}
                </h4>
                <p className="-mt-p25 truncate text-root-size-body-md text-neutral-500">
                  {brand.domain}
                </p>
              </div>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center gap-1 p-4 text-center">
        <p className="text-root-size-body-xl font-500 text-neutral-300">
          We didn't find any brands named{' '}
          <span className="text-neutral-800">"{searchTerm}"</span>
        </p>
        <p className="text-root-size-body-lg font-400 text-neutral-800">
          Check your spelling or try again.
        </p>
        <Button
          variant="tertiary"
          size="md"
          className="mt-4"
          onClick={onResetSearch}
        >
          Reset search
        </Button>
      </div>
    );
  };

  return (
    <div
      className="absolute left-0 top-full z-[999999] mt-1 w-full overflow-y-auto rounded-12 bg-neutral-0 shadow-xl"
      style={{
        maxHeight: '380px',
        minHeight: '200px',
      }}
    >
      {renderHeader()}
      {renderContent()}
    </div>
  );
};
